export interface Cortito {
  userId: string;
  /** Missing from the public database record when the destination is protected. */
  longUrl?: string;
  smallAlias: string;
  clicks: number;
  dateTime: number;
  /** Epoch ms from which the link stops resolving. Absent means it never does. */
  expiresAt?: number;
  /** How many visits the link serves before it stops. Absent means no ceiling. */
  maxClicks?: number;
  /**
   * The labels this cortito is filed under, comma separated.
   *
   * One string and not a list of its own, so the rules can pin it to what it
   * was: counting a visit is a write anybody may make to somebody else's link,
   * and only a single value can be compared that way. The list is held in the
   * browser and filtered there, so nothing is lost by keeping them together.
   */
  tags?: string;
  /**
   * Set while the link is on hold: it stops resolving, no visit is counted and
   * the alias stays taken, so nobody else can claim the name. Absent means the
   * cortito is live, which is what every cortito made before this was.
   */
  paused?: true;
  protected?: true;
  /** Public PBKDF2 salt; the verifier and destination live in a closed node. */
  passwordSalt?: string;
}

/** Twenty characters is a label; anything longer is a sentence. */
const TAG_LENGTH = 20;

/** Trimmed, lower case, and without the comma that separates them. */
export function cleanTag(tag: string): string {
  return tag.replace(/,/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase().slice(0, TAG_LENGTH);
}

export function readTags(link: Cortito): string[] {
  return (link.tags ?? '').split(',').map(cleanTag).filter(Boolean);
}

/** Sorted and without repeats, so two cortitos with the same labels read alike. */
export function writeTags(tags: string[]): string {
  return [...new Set(tags.map(cleanTag).filter(Boolean))].sort().join(',');
}

/** What a cortito can be given when it is made, each left out when it was not asked for. */
export type Extras = Pick<Cortito, 'expiresAt' | 'maxClicks' | 'tags'>;

export function createCortito(
  userId: string,
  longUrl: string,
  smallAlias: string,
  extras: Extras = {},
): Cortito {
  const link: Cortito = { userId, longUrl, smallAlias, clicks: 0, dateTime: Date.now() };
  if (extras.expiresAt) {
    link.expiresAt = extras.expiresAt;
  }
  if (extras.maxClicks) {
    link.maxClicks = extras.maxClicks;
  }
  if (extras.tags) {
    link.tags = extras.tags;
  }
  return link;
}

/**
 * A cortito that has run out: past its date, or having served all the visits it
 * was given. Dropping the limits brings it back.
 */
export function isExpired(link: Cortito, now = Date.now()): boolean {
  return (
    (!!link.expiresAt && now >= link.expiresAt) ||
    (!!link.maxClicks && link.clicks >= link.maxClicks)
  );
}

/**
 * A cortito that does not resolve: put on hold by its owner, or run out on its
 * own. It answers with the 404 page instead of its destination and counts no
 * visit, while the alias stays taken either way.
 *
 * `main.py` and the script inlined in `index.html` decide this same thing, both
 * of them long before this file is loaded.
 */
export function isStopped(link: Cortito, now = Date.now()): boolean {
  return !!link.paused || isExpired(link, now);
}

/** How many days of history the list draws, and the window it reads. */
export const DAYS_SHOWN = 30;

const A_DAY = 86_400_000;

/** How many visits a cortito had each day, kept at `stats/<alias>` under this key. */
export type DailyClicks = Record<string, number>;

/**
 * The day a visit belongs to, in UTC.
 *
 * `main.py` and the script inlined in `index.html` build this same key, each one
 * in its own language, and all three have to agree or a visit lands on a day
 * nobody is looking at.
 */
export function dayKey(when = Date.now()): string {
  return new Date(when).toISOString().slice(0, 10);
}

/** The first day the list still draws, which is also the window it asks for. */
export function firstDayShown(now = Date.now()): string {
  return dayKey(now - (DAYS_SHOWN - 1) * A_DAY);
}

/**
 * The last thirty days as a plain list of numbers ending today, so a day nobody
 * visited reads as a zero instead of a hole. A cortito older than the counter
 * simply has nothing stored and comes back all zeros.
 */
export function dailySeries(daily: DailyClicks, now = Date.now()): number[] {
  return Array.from(
    { length: DAYS_SHOWN },
    (_, index) => daily[dayKey(now - (DAYS_SHOWN - 1 - index) * A_DAY)] ?? 0,
  );
}

/**
 * Where the visits came from, kept at `stats/<alias>/origin` as three tallies:
 * a country, a kind of device and a site, each one a count per key and never a
 * visit of its own. Nobody is followed around — what is stored is that somebody
 * came from Argentina on a phone, not who.
 *
 * A cortito that was never opened since this existed has none of it, which is
 * why every field is optional.
 */
export interface Origins {
  country?: Record<string, number>;
  device?: Record<string, number>;
  from?: Record<string, number>;
}

export const DEVICES = ['mobile', 'tablet', 'desktop'] as const;

export type Device = (typeof DEVICES)[number];

/** The country of a visit nothing could be told about, the way App Engine says it. */
export const UNKNOWN_COUNTRY = 'ZZ';

/** A visit nobody sent: the address was typed, or came from an app that says nothing. */
export const DIRECT = 'direct';

/** A site whose name would not fit a database key, so it is counted all together. */
export const OTHER_SITE = 'other';

const TABLET = /ipad|tablet|playbook|silk|kindle|android(?!.*mobile)/;
const MOBILE = /mobi|iphone|ipod|android|blackberry|opera mini|iemobile|windows phone/;

/**
 * What a visit was made on, read out of the user agent.
 *
 * Tablets first: an Android tablet says `android` too, and only a phone adds
 * `mobile` to it. Anything that matches neither is a desktop, which is the honest
 * answer for everything that does not say — a browser that hides its user agent
 * is far likelier to be a laptop than a phone.
 *
 * `main.py` and the script inlined in `index.html` decide this same thing, each
 * one in its own language, so a visit counts the same wherever it is counted.
 */
export function deviceOf(userAgent: string): Device {
  const agent = userAgent.toLowerCase();
  if (TABLET.test(agent)) {
    return 'tablet';
  }
  return MOBILE.test(agent) ? 'mobile' : 'desktop';
}

/** A database key cannot hold a dot, and a host is mostly dots. */
const SITE_KEY = /^[a-z0-9][a-z0-9_-]{0,63}$/;

/**
 * The site a visit came from, as the key it is counted under: the host on its
 * own, without `www.` and with the dots written as underscores, because a
 * Firebase key may not contain one.
 *
 * A referrer that is missing is `direct`, and a host that would not fit the key
 * is `other` — the rules only take keys of this shape, and one refused would
 * take the country and the device of that visit down with it.
 */
export function siteKey(referrer: string): string {
  if (!referrer) {
    return DIRECT;
  }
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return OTHER_SITE;
  }
  const key = host.replace(/\./g, '_');
  return SITE_KEY.test(key) ? key : OTHER_SITE;
}

/** The host as a person reads it, back from the key it was counted under. */
export function readSite(key: string): string {
  return key.replace(/_/g, '.');
}

/** One line of a breakdown: what it is, how many visits, and how big a share. */
export interface Share {
  key: string;
  visits: number;
  share: number;
}

/**
 * A tally as an ordered breakdown, biggest first, with everything past the top
 * few added up into one last line.
 *
 * The share is of what was counted, not of the cortito's total visits: a cortito
 * older than this counter has visits nothing was ever recorded about, and the
 * bars would never reach the end.
 */
export function breakdown(
  counts: Record<string, number> | undefined,
  limit = 5,
): { rows: Share[]; rest: number; total: number } {
  const entries = Object.entries(counts ?? {})
    .filter(([, visits]) => visits > 0)
    .sort(([firstKey, first], [nextKey, next]) => next - first || firstKey.localeCompare(nextKey));
  const total = entries.reduce((sum, [, visits]) => sum + visits, 0);
  const shown = entries.slice(0, limit);

  return {
    rows: shown.map(([key, visits]) => ({ key, visits, share: total ? visits / total : 0 })),
    rest: total - shown.reduce((sum, [, visits]) => sum + visits, 0),
    total,
  };
}

/** The flag of a country, built out of its two letters. Unknown has no flag. */
export function flagOf(code: string): string {
  if (!/^[A-Z]{2}$/.test(code) || code === UNKNOWN_COUNTRY) {
    return '';
  }
  return String.fromCodePoint(...[...code].map((letter) => letter.charCodeAt(0) + 0x1f1a5));
}
